import { api } from "@/lib/axiosClient";
import { NUMBER_FORMATTING } from "@/shared/config";

// Types
export interface TwitchStream {
  id: string;
  user_id: string;
  user_login: string;
  user_name: string;
  game_id: string;
  game_name: string;
  type: string;
  title: string;
  viewer_count: number;
  started_at: string;
  language: string;
  thumbnail_url: string;
  tag_ids: string[];
  is_mature: boolean;
}

export interface TwitchUser {
  id: string;
  login: string;
  display_name: string;
  type: string;
  broadcaster_type: string;
  description: string;
  profile_image_url: string;
  offline_image_url: string;
  view_count: number;
  created_at: string;
}

// NOTE:
// We cannot call https://api.twitch.tv/helix/* directly from the browser because Twitch does NOT allow CORS.
// All Helix calls must go through our backend proxy endpoints (/api/twitch/*).

// API functions
export class TwitchAPI {
  static async getLiveStreams(
    gameId?: string,
    limit: number = 20,
  ): Promise<TwitchStream[]> {
    try {
      const params: { first: number; game_id?: string } = { first: limit };

      if (typeof gameId === "string" && gameId.trim()) {
        params.game_id = gameId.trim();
      }

      const response = await api.get("/twitch/streams", { params });
      return response.data?.data ?? [];
    } catch {
      return [];
    }
  }

  static async getStreamByUser(
    userLogin: string,
  ): Promise<TwitchStream | null> {
    if (typeof userLogin !== "string" || !userLogin.trim()) {
      return null;
    }
    try {
      const response = await api.get("/twitch/streams", {
        params: { user_login: userLogin },
      });
      return response.data?.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  static async getUserByLogin(userLogin: string): Promise<TwitchUser | null> {
    try {
      const response = await api.get("/twitch/users", {
        params: { login: userLogin },
      });
      return response.data?.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  static async getUsersByLogin(userLogins: string[]): Promise<TwitchUser[]> {
    try {
      // Helix supports repeating `login=` params; our proxy currently supports single login.
      // Keep current usage minimal: fetch sequentially (small lists) or return [] for empty input.
      if (!userLogins.length) return [];
      const results: TwitchUser[] = [];
      for (const login of userLogins) {
        const r = await TwitchAPI.getUserByLogin(login);
        if (r) results.push(r);
      }
      return results;
    } catch {
      return [];
    }
  }

  static async getFollowersCount(userId: string): Promise<number> {
    try {
      const response = await api.get("/twitch/followers", {
        params: { broadcaster_id: userId, first: 1 },
      });
      return response.data?.total ?? 0;
    } catch {
      return 0;
    }
  }
}

// Utility functions
export function formatTwitchThumbnail(
  thumbnailUrl: string,
  width: number = 320,
  height: number = 180,
): string {
  return thumbnailUrl
    .replace("{width}", width.toString())
    .replace("{height}", height.toString());
}

export function formatViewerCount(count: number): string {
  if (count >= NUMBER_FORMATTING.MILLION_THRESHOLD) {
    return `${(count / NUMBER_FORMATTING.MILLION_THRESHOLD).toFixed(NUMBER_FORMATTING.DECIMAL_PLACES)}M`;
  } else if (count >= NUMBER_FORMATTING.THOUSAND_THRESHOLD) {
    return `${(count / NUMBER_FORMATTING.THOUSAND_THRESHOLD).toFixed(NUMBER_FORMATTING.DECIMAL_PLACES)}K`;
  }
  return count.toString();
}

export function getStreamUrl(userLogin: string): string {
  return `https://www.twitch.tv/${userLogin}`;
}
